import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Image as ImageIcon, Video, Lock } from "lucide-react";

const QUICK_PROMPTS = [
  "Matte-black product packaging on marble — premium commercial photography",
  "Vertical neo-noir music video shot, dramatic lighting, urban rooftop",
  "Luxury watch closeup on dark velvet, soft rim light, brand commercial",
  "Minimalist skincare bottle on cream backdrop, soft daylight, social ad",
];

export default function HomepageCreator() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("image"); // image | video
  const [aspect, setAspect] = useState("9:16");

  const onSubmit = (e) => {
    e?.preventDefault();
    if (prompt.trim().length < 4) return;
    // Stash the prompt in sessionStorage so it auto-fills after signup/login.
    sessionStorage.setItem("ybm_pending_prompt", JSON.stringify({ prompt, kind, aspect }));
    navigate("/register");
  };

  return (
    <section
      id="creator-box"
      data-testid="homepage-creator-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-white/10 overflow-hidden"
    >
      <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/8 blur-[140px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Try it now</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#F4F4F0] leading-[1.02]">
            Create freely.<br />Download what you keep.
          </h2>
          <p className="mt-6 text-[#A0A0A0] max-w-2xl mx-auto leading-relaxed">
            Type what you want. Preview after sign-up. Free downloads include a watermark — paid plans remove it.
            Only downloaded videos count toward your plan.
          </p>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="border border-[#2A2A2A] bg-[#121212] p-6 md:p-8"
          data-testid="homepage-creator-form"
        >
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#A0A0A0] mb-3">
              Describe your ad, promo or scene
            </span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="A matte black coffee bag on a marble countertop, premium product hero shot, soft side lighting..."
              data-testid="homepage-creator-prompt"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-3 text-[#F4F4F0] placeholder-[#5A5A5A] transition-colors resize-y text-base"
            />
          </label>

          {/* Quick prompts */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0] self-center mr-1">Try:</span>
            {QUICK_PROMPTS.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPrompt(p)}
                data-testid={`homepage-quickprompt-${i}`}
                className="text-xs text-[#A0A0A0] hover:text-[#D4AF37] border border-[#2A2A2A] hover:border-[#D4AF37]/60 px-3 py-1.5 transition-colors max-w-full truncate"
              >
                {p.length > 56 ? p.slice(0, 56) + "…" : p}
              </button>
            ))}
          </div>

          {/* Kind + aspect selectors */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#A0A0A0] mb-2">Output</span>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setKind("image")} data-testid="homepage-kind-image"
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 text-sm border transition-colors ${
                    kind === "image" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
                  }`}>
                  <ImageIcon className="w-4 h-4" /> Image
                </button>
                <button type="button" onClick={() => setKind("video")} data-testid="homepage-kind-video"
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 text-sm border transition-colors ${
                    kind === "video" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
                  }`}>
                  <Video className="w-4 h-4" /> Video
                </button>
              </div>
            </div>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#A0A0A0] mb-2">Aspect</span>
              <div className="grid grid-cols-3 gap-2">
                {["9:16", "1:1", "16:9"].map((a) => (
                  <button key={a} type="button" onClick={() => setAspect(a)} data-testid={`homepage-aspect-${a}`}
                    className={`px-3 py-2.5 text-sm border transition-colors ${
                      aspect === a ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
                    }`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <p className="text-xs text-[#A0A0A0] inline-flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
              Sign up to preview & download — every render debits credits from your wallet (top up from $5).
            </p>
            <button
              type="submit"
              disabled={prompt.trim().length < 4}
              data-testid="homepage-creator-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-6 py-3 hover:bg-[#F0C84B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <Sparkles className="w-4 h-4" />
              Sign Up & Generate
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
